import { JSX, useEffect, useState } from 'react';
import {
    Autocomplete,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Stack,
    TextField,
} from '@mui/material';
import { choicesOf, CsvImportConfig, ImportColumn, ImportLookups, ImportOption, ImportValues } from './csv-import-types';
import { isRequired, splitIds } from './csv-import';

interface ImportRowDialogProps {
    config: CsvImportConfig;
    /** The row being edited, or null when the dialog is closed. */
    values: ImportValues | null;
    title: string;
    problems: Record<string, string>;
    lookups: ImportLookups;
    onSave: (values: ImportValues) => void;
    onClose: () => void;
}

/**
 * Every field of one imported row as a form. The grid edits short cells in place; this is where
 * long text and lists of ids are edited, and where a row is read in full.
 */
export const ImportRowDialog = (props: ImportRowDialogProps): JSX.Element => {
    const { config, values, title, problems, lookups, onSave, onClose } = props;
    const [draft, setDraft] = useState<ImportValues>({});

    useEffect((): void => {
        if (values) {
            setDraft({ ...values });
        }
    }, [values]);

    const set = (key: string, value: string): void => setDraft((current: ImportValues) => ({ ...current, [key]: value }));

    const field = (column: ImportColumn): JSX.Element => {
        const value: string = draft[column.key] ?? '';
        // A problem is shown until the field changes, then left for Validate to judge again.
        const problem: string | undefined = values && value === values[column.key] ? problems[column.key] : undefined;
        const label: string = column.label + (isRequired(column, draft) ? ' *' : '');
        const helperText: string | undefined = problem ?? column.helper;
        const options: ImportOption[] = column.lookup ? lookups[column.lookup] ?? [] : [];

        const choices: ImportOption[] = choicesOf(column) ?? [];
        if (column.kind === 'choice' || column.kind === 'boolean') {
            return (
                <TextField key={ column.key } select label={ label } value={ value } error={ !!problem }
                           helperText={ helperText } onChange={ (event) => set(column.key, event.target.value) }>
                    <MenuItem value=""><em>None</em></MenuItem>
                    { choices.map((choice: ImportOption) => (
                        <MenuItem key={ choice.value } value={ choice.value }>{ choice.label }</MenuItem>
                    )) }
                    { value !== '' && !choices.some((choice) => choice.value === value) &&
                        <MenuItem value={ value }>{ value }</MenuItem>
                    }
                </TextField>
            );
        }

        if (column.kind === 'reference' || column.kind === 'references') {
            const multiple: boolean = column.kind === 'references';
            const ids: string[] = multiple ? splitIds(value) : (value === '' ? [] : [value]);
            // An id the lookup does not hold is kept as its own option, so it shows (and can be
            // removed) rather than vanishing from the field.
            const selected: ImportOption[] = ids.map((id: string): ImportOption =>
                options.find((option: ImportOption): boolean => option.value === id) ?? { value: id, label: 'Unknown id ' + id });

            return (
                <Autocomplete<ImportOption, boolean>
                    key={ column.key }
                    multiple={ multiple }
                    options={ options }
                    value={ multiple ? selected : (selected[0] ?? null) }
                    isOptionEqualToValue={ (option: ImportOption, chosen: ImportOption): boolean => option.value === chosen.value }
                    getOptionLabel={ (option: ImportOption): string => option.label }
                    onChange={ (_event, chosen) => {
                        const list: ImportOption[] = Array.isArray(chosen) ? chosen : (chosen ? [chosen] : []);
                        set(column.key, list.map((option: ImportOption): string => option.value).join(';'));
                    } }
                    renderInput={ (params) => (
                        <TextField { ...params } label={ label } error={ !!problem } helperText={ helperText } />
                    ) }
                />
            );
        }

        return (
            <TextField key={ column.key } label={ label } value={ value } error={ !!problem } helperText={ helperText }
                       multiline={ column.kind === 'longText' } minRows={ column.kind === 'longText' ? 4 : undefined }
                       maxRows={ 20 }
                       onChange={ (event) => set(column.key, event.target.value) } />
        );
    };

    return (
        <Dialog open={ values !== null } onClose={ onClose } maxWidth="md" fullWidth>
            <DialogTitle>{ title }</DialogTitle>
            <DialogContent>
                <Stack spacing={ 2 } sx={ { pt: 1 } }>
                    { config.columns.map(field) }
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={ onClose }>Cancel</Button>
                <Button variant="contained" onClick={ () => onSave(draft) }>Apply</Button>
            </DialogActions>
        </Dialog>
    );
};
